export type BitmapFontFileKind = 'page' | 'xfi' | 'meta'

export class BitmapFontGlyph {
  public char = ''
  public index = 0
  public width = 0
  public height = 0

  constructor(init?: Partial<BitmapFontGlyph>) {
    Object.assign(this, init)
  }
}

export class BitmapFontFile {
  public fileName = ''
  public kind: BitmapFontFileKind = 'page'
  public mimeType = 'application/octet-stream'
  public bytes: Uint8Array = new Uint8Array(0)
  public objectUrl = ''

  constructor(init?: Partial<BitmapFontFile>) {
    Object.assign(this, init)
  }

  toBlob(): Blob {
    const buffer = this.bytes.buffer.slice(
      this.bytes.byteOffset,
      this.bytes.byteOffset + this.bytes.byteLength,
    ) as ArrayBuffer
    return new Blob([buffer], { type: this.mimeType })
  }

  ensureObjectUrl(): string {
    if (!this.objectUrl) {
      this.objectUrl = URL.createObjectURL(this.toBlob())
    }
    return this.objectUrl
  }

  revokeObjectUrl(): void {
    if (!this.objectUrl) return
    URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = ''
  }
}

export class BitmapFontPackage {
  public fontName = ''
  public fontSize = 0
  /** 工程字体设置中的存储路径，用于判断切换 TTF 后是否可复用缓存。 */
  public sourceFontKey = ''
  public rows = 0
  public cols = 0
  public cellWidth = 0
  public cellHeight = 0
  /** 纹理页是否为固定 rows×cellHeight 栅格；旧版可变高度页不可复用。 */
  public pageLayoutFixed = false
  public glyphs: BitmapFontGlyph[] = []
  public files: BitmapFontFile[] = []

  constructor(init?: Partial<BitmapFontPackage>) {
    Object.assign(this, init)
    this.glyphs = (init?.glyphs ?? []).map((item) => new BitmapFontGlyph(item))
    this.files = (init?.files ?? []).map((item) => new BitmapFontFile(item))
  }
}

export default class BitmapFontData {
  public packages: BitmapFontPackage[] = []
  public extraFiles: BitmapFontFile[] = []
  /** 手动补充的字符集，生成时会和工程自动收集文本合并。 */
  public extraText = ''

  constructor(init?: Partial<BitmapFontData>) {
    Object.assign(this, init)
    this.packages = (init?.packages ?? []).map((item) => new BitmapFontPackage(item))
    this.extraFiles = (init?.extraFiles ?? []).map((item) => new BitmapFontFile(item))
  }

  dispose(): void {
    for (const file of this.files) {
      file.revokeObjectUrl()
    }
  }

  get primaryPackage(): BitmapFontPackage | null {
    return this.packages[0] ?? null
  }

  get fontName(): string {
    return this.primaryPackage?.fontName ?? ''
  }

  get fontSize(): number {
    return this.primaryPackage?.fontSize ?? 0
  }

  get rows(): number {
    return this.primaryPackage?.rows ?? 0
  }

  get cols(): number {
    return this.primaryPackage?.cols ?? 0
  }

  get cellWidth(): number {
    return this.primaryPackage?.cellWidth ?? 0
  }

  get cellHeight(): number {
    return this.primaryPackage?.cellHeight ?? 0
  }

  get glyphs(): BitmapFontGlyph[] {
    return this.primaryPackage?.glyphs ?? []
  }

  get files(): BitmapFontFile[] {
    return [...this.packages.flatMap((item) => item.files), ...this.extraFiles]
  }
}
